import {LanguageService} from './language.service';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {Subject} from 'rxjs/Subject';

// #########################################################################
//
// This service is used to keep track of all information about the previous
// commands. We keep track of the following thing and provide an interface to access
// those information from a component:
//      - commands
//      - current command index
//      
// We can add arbitrary Strings to the commands array and it will be automatically
// appended to the historyComponent because they are bound together 
//
// #########################################################################

declare var ace:any;

export class CommandsService {

  public commands = [{}];
  private historyCommandsOnly: string[] = [''];
  private currentCommandIndex: number = -1;
  public space: Subject<Object[]> = new BehaviorSubject<Object[]>(null);

  private lang: LanguageService;

  // Ace editor for input line
  public inputLine: any;

  private errorCol = "#ff8000";
  private commandCol = "black";

  // TODO: pretty ugly hack. How can the language service be passed down here?
  public setLangService(lang) {
    this.lang = lang;
    this.lang.space.subscribe((val) => {
      if (val != undefined) {
        this.checkPlaceholderText();
      }
    });
  }

  public checkPlaceholderText() {
		var shouldShow = !this.inputLine.session.getValue().length;
    var node = this.inputLine.renderer.emptyMessageNode;
    
		if (shouldShow) {
      node.textContent = this.lang.getLang()['enterCommand'];
		} else {
      node.textContent = '';
		}
  }
  
  public initInputLine(addElem) {
		this.inputLine = ace.edit('inputEditor');
		this.inputLine.setTheme('ace/theme/kr_theme'); // kuroir
    this.inputLine.getSession().setMode('ace/mode/python');

		// Change appearance to resemble input field instead of code editor
		this.inputLine.setShowPrintMargin(false);
		this.inputLine.renderer.setShowGutter(false);
    this.inputLine.setOption("highlightActiveLine", false);
    this.inputLine.setOption("maxLines", 1);
		this.inputLine.renderer.setScrollMargin(15, 0);
    this.inputLine.renderer.updateFontSize();
    this.inputLine.commands.bindKey('Shift-Enter', 'null');
    this.inputLine.commands.bindKeys({"Ctrl-L": null, "Ctrl-F": null});

    // Intercept 'enter' keystrokes and remove it, execute commands instead
		this.inputLine.commands.addCommand({
			name: "exec",
			bindKey: {
				win: "Enter",
				mac: "Enter"
			},
			exec: function (editor, line) {
				if (!this.inputLine.getReadOnly()) {
					let commands = this.inputLine.getSession().getLine(0);
					addElem(commands);
				}
			}.bind(this),
			readOnly: true
    });
    
	/*
		Filter out newline characters on paste by creating a proxy
		function which is used to inject code into the onPaste
		function of inputLine
	*/
	(function() {
		this.inputLine.originalOnPaste = this.inputLine.onPaste;
		this.inputLine.onPaste = function(text, event) {
			let incoming = text.replace(/(\r\n|\n|\r)/gm, " ");
			this.inputLine.originalOnPaste(incoming, event);
		}.bind(this);
	}).bind(this)();

    // Add empty message node for placeholder text
    let node = this.inputLine.renderer.emptyMessageNode = document.createElement("div");
    node.textContent = this.lang.getLang()['enterCommand'];
    node.style.color = "gray";
    node.style.padding = "15px 5px";
    this.inputLine.renderer.scroller.appendChild(node);

		// Add placeholder text if nothing is written
		this.checkPlaceholderText();
  	this.inputLine.on("input", this.checkPlaceholderText.bind(this));
  }

  public setCommandLineText(text) {
    this.inputLine.setValue(text);
    const col = this.inputLine.session.getLine(0).length;
    this.inputLine.selection.moveTo(0, col);
  }

  public getCommandLineText() {
    return this.inputLine.getValue();
  }

  public addString(text: string) {
    this.commands.push({text: text, color:this.errorCol});
    this.space.next(this.commands);
  }

  public addCommand(text: string){
    this.commands.push({text: text, color: this.commandCol});
    this.historyCommandsOnly.push(text);
    this.space.next(this.commands);
  }

  public addErrorMessage(errormessages: string[], input: string){
		let headlineChar = "~";
		let startErrorlineChars = "~ ";
		let headlineChars;
		let headlineLength;

		//find maximum size among all error messages
		let maxSize = 0;
		for(var errorMessageIndex = 0; errorMessageIndex<errormessages.length; errorMessageIndex++){
			if(errormessages[errorMessageIndex].length>maxSize){
				maxSize = errormessages[errorMessageIndex].length;
			}
		}

		//show headline
		headlineChars = Array(startErrorlineChars.length+maxSize+1).join(headlineChar);
		this.addString(headlineChars);

		//show all error messages
		for(var errorMessageIndex = 0; errorMessageIndex<errormessages.length; errorMessageIndex++){
			this.addString(startErrorlineChars+errormessages[errorMessageIndex]);
		}

		//show user input
		this.addString(startErrorlineChars+input.toUpperCase());

		//show headline
		this.addString(headlineChars);
	}
  
  public deleteAll(){
    this.commands = [''];
    this.space.next(this.commands);
  }

  public getCommand(keycode: number) {
    // If input line is disabled, ignore keystrokes
    if (this.inputLine.getReadOnly()) {
      return;
    }

    if (keycode === 38) {
      // Case Arrow Up
      this.currentCommandIndex--;
      if (this.getCommandLineText() === '') {
      /*
        TODO: in this case, wouldn't it be better to just stay on the
        last command instead of looping around?
      */
        this.currentCommandIndex = this.historyCommandsOnly.length - 1;
      }
      if (this.currentCommandIndex >= 1) {
        this.setCommandLineText(this.historyCommandsOnly[this.currentCommandIndex]);

      } else {
        this.setCommandLineText('');
      }
    } else {
      // Case Arrow Down
      if (this.currentCommandIndex < this.historyCommandsOnly.length - 1 && this.currentCommandIndex >= -1) {
        this.currentCommandIndex++;
        this.setCommandLineText(this.historyCommandsOnly[this.currentCommandIndex]);
      } else {
        this.setCommandLineText('');
      }
    }
  }

  removeAllCommandFromHistory(){
    this.commands = [{}];
    this.historyCommandsOnly = [''];
    this.currentCommandIndex = -1;
  }

  getData() {
    return this.commands;
  }

}



// WEBPACK FOOTER //
// ./src/app/services/commands.service.ts